import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleSignInButton from './GoogleSignInButton';
import cartService from '../services/cartService';
import pendingActionService from '../services/pendingActionService';
import { trackLogin, trackSignupComplete, trackSignupStart, identifyUser } from '../utils/analytics';
import { getPostAuthRedirect, stashPostAuthRedirect } from '../utils/authRedirect';
import { useDebounce } from 'use-debounce';
import useCuisineOptions from '../hooks/useCuisineOptions';
import MenuItemDetail from './MenuItemDetail';
import SEO from './SEO';
import { resolveHasLiked, resolveLikesCount } from '../utils/menuLikes';
import './MealPlanSuggestion.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

const COOK_TIME_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '15', label: '15 min or less' },
  { value: '30', label: '30 min or less' },
  { value: '45', label: '45 min or less' },
  { value: '60', label: '1 hour or less' }
];

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`
  };
}

function formatDayLabel(dateStr) {
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  });
}

function formatMealType(mealType) {
  if (!mealType) return 'Meal';
  return mealType.charAt(0).toUpperCase() + mealType.slice(1);
}

function formatShortDay(dateStr) {
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
}

/** The seven dates a suggestion may be moved between, starting at its weekStart. */
function weekDates(weekStart) {
  if (!weekStart) return [];
  const start = new Date(`${weekStart}T12:00:00`);
  if (Number.isNaN(start.getTime())) return [];
  return Array.from({ length: 7 }, (_, offset) => {
    const date = new Date(start);
    date.setDate(start.getDate() + offset);
    return date.toISOString().split('T')[0];
  });
}

function groupMealsByDate(meals = []) {
  const groups = [];
  const indexByDate = new Map();
  meals.forEach((meal) => {
    const date = meal.date || 'upcoming';
    if (!indexByDate.has(date)) {
      indexByDate.set(date, groups.length);
      groups.push({ date, meals: [] });
    }
    groups[indexByDate.get(date)].meals.push(meal);
  });
  return groups;
}

const MealPlanSuggestion = () => {
  const { publicId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const intent = searchParams.get('intent') || 'review';
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const isLoggedIn = Boolean(user && localStorage.getItem('token'));

  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(isLoggedIn);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [swapMeal, setSwapMeal] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [alternativesLoading, setAlternativesLoading] = useState(false);
  const [altHasMore, setAltHasMore] = useState(false);
  const [altPage, setAltPage] = useState(1);
  const [altTotal, setAltTotal] = useState(0);
  const [swapSearch, setSwapSearch] = useState('');
  const [swapFilters, setSwapFilters] = useState({ cuisine: '', dietary: [], maxMinutes: '' });
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dietaryOptions, setDietaryOptions] = useState([]);

  const { cuisines: cuisineOptions } = useCuisineOptions();
  const [debouncedSwapSearch] = useDebounce(swapSearch, 300);

  useEffect(() => {
    fetch(`${API_URL}/menu/dietary-options`)
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setDietaryOptions((Array.isArray(data) ? data : []).filter(Boolean)))
      .catch(() => setDietaryOptions([]));
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      stashPostAuthRedirect(`${window.location.pathname}${window.location.search}`);
    }
  }, [isLoggedIn]);

  const loadSuggestion = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/mealplan/suggestions/${encodeURIComponent(id)}`, {
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Could not load this meal plan');
      }
      setSuggestion(data.suggestion);
      if (data.suggestion?.publicId && data.suggestion.publicId !== id) {
        const nextSearch = searchParams.toString();
        navigate(`/meal-plan/suggestion/${data.suggestion.publicId}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    if (isLoggedIn && publicId) {
      loadSuggestion(publicId);
    }
  }, [isLoggedIn, publicId, loadSuggestion]);

  const includedCount = (suggestion?.meals || []).filter((meal) => meal.included).length;

  const patchMeals = async (meals) => {
    const id = suggestion?.publicId || publicId;
    const response = await fetch(`${API_URL}/mealplan/suggestions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ meals })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Could not update this meal plan');
    }
    setSuggestion(data.suggestion);
    return data.suggestion;
  };

  const toggleIncluded = async (meal) => {
    const nextIncluded = !meal.included;
    setSuggestion((current) => ({
      ...current,
      meals: current.meals.map((item) => item.id === meal.id ? { ...item, included: nextIncluded } : item)
    }));
    try {
      await patchMeals([{ id: meal.id, included: nextIncluded }]);
    } catch (err) {
      toast.error(err.message);
      loadSuggestion(suggestion?.publicId || publicId);
    }
  };

  const moveMeal = async (meal, changes) => {
    setSuggestion((current) => ({
      ...current,
      meals: current.meals.map((item) => item.id === meal.id ? { ...item, ...changes } : item)
    }));
    try {
      await patchMeals([{ id: meal.id, ...changes }]);
    } catch (err) {
      toast.error(err.message);
      loadSuggestion(suggestion?.publicId || publicId);
    }
  };

  const fetchAlternatives = useCallback(async (meal, { page = 1, append = false } = {}) => {
    setAlternativesLoading(true);
    try {
      const id = suggestion?.publicId || publicId;
      const params = new URLSearchParams({ mealId: meal.id, page: String(page) });
      if (debouncedSwapSearch.trim()) params.set('search', debouncedSwapSearch.trim());
      if (swapFilters.cuisine) params.set('cuisine', swapFilters.cuisine);
      if (swapFilters.dietary.length) params.set('dietary', swapFilters.dietary.join(','));
      if (swapFilters.maxMinutes) params.set('maxMinutes', swapFilters.maxMinutes);

      const response = await fetch(
        `${API_URL}/mealplan/suggestions/${encodeURIComponent(id)}/alternatives?${params.toString()}`,
        { headers: authHeaders() }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Could not load other recipes');
      }
      setAlternatives((current) => append ? [...current, ...(data.alternatives || [])] : (data.alternatives || []));
      setAltHasMore(Boolean(data.hasMore));
      setAltTotal(data.total || 0);
      setAltPage(page);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAlternativesLoading(false);
    }
  }, [suggestion?.publicId, publicId, debouncedSwapSearch, swapFilters]);

  const openSwap = (meal) => {
    setSwapMeal(meal);
    setAlternatives([]);
    setSwapSearch('');
    setSwapFilters({ cuisine: '', dietary: [], maxMinutes: '' });
  };

  // Re-query whenever the picker opens or its search/filters change.
  useEffect(() => {
    if (!swapMeal) return;
    fetchAlternatives(swapMeal, { page: 1 });
  }, [swapMeal?.id, debouncedSwapSearch, swapFilters, fetchAlternatives]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSwapDietary = (value) => {
    setSwapFilters((current) => ({
      ...current,
      dietary: current.dietary.includes(value)
        ? current.dietary.filter((item) => item !== value)
        : [...current.dietary, value]
    }));
  };

  const chooseAlternative = async (alt) => {
    if (!swapMeal) return;
    try {
      setSaving(true);
      await patchMeals([{ id: swapMeal.id, menuItemId: alt.menuItemId, included: true }]);
      toast.success(`Swapped in ${alt.title}`);
      setSwapMeal(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Show the recipe in an overlay instead of routing to /menu/:id, which would
   * strand the user on the menu page when they close it.
   */
  const openRecipe = async (meal) => {
    if (!meal.menuId) return;
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/menu/${encodeURIComponent(meal.menuId)}`, {
        headers: localStorage.getItem('token') ? authHeaders() : {}
      });
      if (!response.ok) throw new Error('Could not load this recipe');
      const item = await response.json();
      setDetailItem({
        ...item,
        likes: resolveLikesCount(item),
        isLiked: resolveHasLiked(item, user)
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const adoptAndContinue = async ({ addToCart = false } = {}) => {
    if (!includedCount) {
      toast.error('Keep at least one meal to add this week');
      return;
    }
    try {
      setSaving(true);
      const id = suggestion?.publicId || publicId;
      const response = await fetch(`${API_URL}/mealplan/suggestions/${encodeURIComponent(id)}/adopt`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Could not add this week to your meal plan');
      }
      const kept = (data.suggestion?.meals || suggestion.meals).filter((meal) => meal.included && meal.menuItemId);
      if (addToCart) {
        let added = 0;
        for (const meal of kept) {
          try {
            await cartService.addMenuToCart(meal.menuItemId);
            added += 1;
          } catch (err) {
            console.error('Failed to add suggested meal to cart', err);
          }
        }
        toast.success(`Added ${data.added} meals to your plan and ${added} to your cart`);
        navigate('/cart');
        return;
      }
      toast.success(`Added ${data.added} meals to your meal plan`);
      navigate('/meal-plan');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    trackSignupStart('weekly_suggestion');
    try {
      if (!credentialResponse.credential) {
        toast.error('Missing authentication credentials');
        return;
      }
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential })
      });
      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }
      const data = await response.json();
      const userData = {
        ...data.user,
        sub: data.user.sub || data.user.id || data.user.email
      };
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);
      const userId = userData.sub || userData.email;
      if (data.isNewUser) {
        trackSignupComplete('weekly_suggestion', userId);
      } else {
        trackLogin('weekly_suggestion', userId);
      }
      identifyUser(userId, { email: userData.email });
      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      const next = getPostAuthRedirect({
        isNewUser: data.isNewUser,
        pendingRedirectUrl: redirectUrl,
        pendingSubscription: localStorage.getItem('pendingSubscription'),
        defaultHref: `${window.location.pathname}${window.location.search}`
      });
      window.location.href = next || `${window.location.pathname}${window.location.search}`;
    } catch (err) {
      toast.error(err.message || 'Sign in failed');
    }
  };

  const groups = groupMealsByDate(suggestion?.meals || []);
  const weekLabel = suggestion?.weekStart
    ? `Week of ${formatDayLabel(suggestion.weekStart)}`
    : 'This week\'s meal ideas';

  const heading = intent === 'cart'
    ? 'Add this week to your shopping list'
    : intent === 'adjust'
      ? 'Adjust this week\'s meal suggestions'
      : 'Review this week\'s meal suggestions';

  const availableDates = weekDates(suggestion?.weekStart);

  if (!isLoggedIn) {
    return (
      <div className="mp-suggestion-page">
        <SEO title="This week's meal ideas" description="Review, swap, and add this week's meal ideas to your FreshFitLab meal plan." robots="noindex,nofollow" />
        <div className="mp-suggestion-signin">
          <h1>Your week of meal ideas is ready</h1>
          <p>Sign in to keep the meals you want, swap anything else, then add them to your meal plan or shopping list.</p>
          <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={() => toast.error('Sign in failed')} />
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="mp-suggestion-page"><div className="mp-suggestion-status">Loading this week's meals...</div></div>;
  }

  if (error || !suggestion) {
    return (
      <div className="mp-suggestion-page">
        <div className="mp-suggestion-status error">
          <p>{error || 'This meal plan is no longer available.'}</p>
          <Link to="/meal-plan">Go to my meal plan</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-suggestion-page">
      <SEO title={heading} description="Keep, swap, or drop recipes from this week's meal ideas, then add them to your meal plan." robots="noindex,nofollow" />
      <header className="mp-suggestion-header">
        <p className="mp-suggestion-kicker">{weekLabel}</p>
        <h1>{heading}</h1>
        <p className="mp-suggestion-lead">
          {suggestion.summary || 'Uncheck meals you do not want, swap a recipe, then add the rest to your plan. This does not overwrite other days on your calendar.'}
        </p>
      </header>

      <div className="mp-suggestion-days">
        {groups.map((group) => (
          <section key={group.date} className="mp-suggestion-day">
            <h2>{formatDayLabel(group.date)}</h2>
            {group.meals.map((meal) => (
              <article key={meal.id} className={`mp-suggestion-card ${meal.included ? '' : 'is-dropped'}`}>
                <button
                  type="button"
                  className={`mp-suggestion-keep ${meal.included ? 'on' : ''}`}
                  onClick={() => toggleIncluded(meal)}
                  aria-pressed={meal.included}
                >
                  {meal.included ? 'Keeping' : 'Dropped'}
                </button>
                {meal.image ? (
                  <img src={meal.image} alt="" className="mp-suggestion-thumb" />
                ) : (
                  <div className="mp-suggestion-thumb placeholder">🥗</div>
                )}
                <div className="mp-suggestion-card-body">
                  <p className="mp-suggestion-meal-type">{formatMealType(meal.mealType)}</p>
                  <h3>{meal.title || "Chef's choice"}</h3>
                  <p className="mp-suggestion-meta">{[meal.cuisine, meal.totalTime].filter(Boolean).join(' · ')}</p>

                  <div className="mp-suggestion-slot">
                    <label>
                      <span>Day</span>
                      <select
                        value={meal.date}
                        onChange={(event) => moveMeal(meal, { date: event.target.value })}
                      >
                        {availableDates.map((date) => (
                          <option key={date} value={date}>{formatShortDay(date)}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Meal</span>
                      <select
                        value={meal.mealType}
                        onChange={(event) => moveMeal(meal, { mealType: event.target.value })}
                      >
                        {MEAL_TYPES.map((type) => (
                          <option key={type} value={type}>{formatMealType(type)}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="mp-suggestion-card-actions">
                    <button type="button" onClick={() => openSwap(meal)}>Swap recipe</button>
                    {meal.menuId && (
                      <button
                        type="button"
                        className="mp-suggestion-link-btn"
                        onClick={() => openRecipe(meal)}
                        disabled={detailLoading}
                      >
                        View recipe
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </section>
        ))}
      </div>

      <footer className="mp-suggestion-footer">
        <p>{includedCount} meal{includedCount === 1 ? '' : 's'} selected</p>
        <div className="mp-suggestion-footer-actions">
          {intent === 'cart' ? (
            <>
              <button
                type="button"
                className="mp-suggestion-secondary"
                disabled={saving || !includedCount}
                onClick={() => adoptAndContinue({ addToCart: false })}
              >
                Add to my meal plan
              </button>
              <button
                type="button"
                className="mp-suggestion-primary"
                disabled={saving || !includedCount}
                onClick={() => adoptAndContinue({ addToCart: true })}
              >
                {saving ? 'Adding...' : 'Add to shopping list'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="mp-suggestion-secondary"
                disabled={saving || !includedCount}
                onClick={() => adoptAndContinue({ addToCart: true })}
              >
                Add to shopping list
              </button>
              <button
                type="button"
                className="mp-suggestion-primary"
                disabled={saving || !includedCount}
                onClick={() => adoptAndContinue({ addToCart: false })}
              >
                {saving ? 'Adding...' : 'Add to my meal plan'}
              </button>
            </>
          )}
        </div>
      </footer>

      {swapMeal && (
        <div className="mp-suggestion-swap-overlay" onClick={() => setSwapMeal(null)}>
          <div className="mp-suggestion-swap" onClick={(event) => event.stopPropagation()}>
            <header>
              <h2>Swap {swapMeal.title}</h2>
              <button type="button" onClick={() => setSwapMeal(null)} aria-label="Close">×</button>
            </header>

            <div className="mp-suggestion-swap-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                value={swapSearch}
                onChange={(event) => setSwapSearch(event.target.value)}
                placeholder="Search recipes, ingredients, cuisines..."
                autoFocus
              />
            </div>

            <div className="mp-suggestion-swap-filters">
              <select
                value={swapFilters.cuisine}
                onChange={(event) => setSwapFilters((current) => ({ ...current, cuisine: event.target.value }))}
              >
                <option value="">All cuisines</option>
                {cuisineOptions.map((cuisine) => (
                  <option key={cuisine} value={cuisine}>{cuisine}</option>
                ))}
              </select>
              <select
                value={swapFilters.maxMinutes}
                onChange={(event) => setSwapFilters((current) => ({ ...current, maxMinutes: event.target.value }))}
              >
                {COOK_TIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {dietaryOptions.length > 0 && (
              <div className="mp-suggestion-swap-diet">
                {dietaryOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={swapFilters.dietary.includes(option) ? 'selected' : ''}
                    onClick={() => toggleSwapDietary(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            <p className="mp-suggestion-swap-count">
              {alternativesLoading && alternatives.length === 0
                ? 'Finding recipes...'
                : `${altTotal} recipe${altTotal === 1 ? '' : 's'} match`}
            </p>

            {!alternativesLoading && alternatives.length === 0 ? (
              <p className="mp-suggestion-swap-empty">No recipes match these filters. Try clearing the search.</p>
            ) : (
              <div className="mp-suggestion-alts">
                {alternatives.map((alt) => (
                  <button
                    key={alt.menuItemId}
                    type="button"
                    className="mp-suggestion-alt"
                    disabled={saving}
                    onClick={() => chooseAlternative(alt)}
                  >
                    {alt.image ? <img src={alt.image} alt="" /> : <span className="mp-suggestion-thumb placeholder">🥗</span>}
                    <span>
                      <strong>{alt.title}</strong>
                      <em>{[alt.cuisine, alt.totalTime].filter(Boolean).join(' · ')}</em>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {altHasMore && (
              <button
                type="button"
                className="mp-suggestion-swap-more"
                disabled={alternativesLoading}
                onClick={() => fetchAlternatives(swapMeal, { page: altPage + 1, append: true })}
              >
                {alternativesLoading ? 'Loading...' : 'Show more recipes'}
              </button>
            )}
          </div>
        </div>
      )}

      {detailItem && (
        <MenuItemDetail item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </div>
  );
};

export default MealPlanSuggestion;
